import { Immutable } from "immer";
import React from "react";

import { PasswordRevealInput } from "../PasswordRevealInput";

// Structural view of the Python `FormFieldData` / `FormChoice` NamedTuples
// (tsicommon.cms.react_forms). Typed locally rather than against the per-package
// generated `@reactivated` type so this shared renderer is package-agnostic.
type FormChoice = Immutable<{
    value: string;
    label: string;
    selected: boolean;
    input_id: string;
}>;
export type FormFieldData = Immutable<{
    kind: string;
    name: string;
    field_id: string;
    input_type: string;
    value: string;
    required: boolean;
    checked: boolean;
    maxlength: string;
    step: string;
    cols: string;
    rows: string;
    choices: FormChoice[];
}>;

const num = (v: string): number | undefined =>
    v === "" ? undefined : Number(v);

// Django has no password widget kind, so a password field arrives as
// kind="text" with input_type="password".
const isPasswordField = (field: FormFieldData): boolean =>
    field.kind === "text" && field.input_type === "password";

// The fields FieldControl wraps in a div. They cannot go inside a <p> — the
// browser re-parses the div back out of it.
const fieldRendersBlock = (field: FormFieldData): boolean =>
    field.kind === "radio" ||
    field.kind === "checkboxes" ||
    isPasswordField(field);

/**
 * Renders one Wagtail form-builder field, mirroring Django's `{{ field }}`
 * widget output (no label/help/errors). Choice widgets selected-state comes from
 * `choices[].selected`.
 *
 * Password fields are the one exception: they come back wrapped in a div, with
 * a Show / Hide button Django never renders.
 */
export const FieldControl = ({
    field,
    placeholder,
}: Immutable<{
    field: FormFieldData;
    placeholder?: string;
}>): React.JSX.Element => {
    const required = field.required || undefined;
    // #35342: these used to fall through to a plain input and lose the
    // Show / Hide button.
    if (isPasswordField(field)) {
        return (
            <PasswordRevealInput
                inputProps={{
                    id: field.field_id,
                    name: field.name,
                    required: required,
                    placeholder: placeholder || undefined,
                    maxLength: num(field.maxlength),
                    defaultValue: field.value,
                    autoComplete: "new-password",
                }}
            />
        );
    }
    switch (field.kind) {
        case "textarea":
            return (
                <textarea
                    id={field.field_id}
                    name={field.name}
                    required={required}
                    placeholder={placeholder || undefined}
                    cols={num(field.cols)}
                    rows={num(field.rows)}
                    maxLength={num(field.maxlength)}
                    defaultValue={field.value}
                />
            );
        case "checkbox":
            return (
                <input
                    type="checkbox"
                    id={field.field_id}
                    name={field.name}
                    required={required}
                    defaultChecked={field.checked || undefined}
                    value={field.value || undefined}
                />
            );
        case "hidden":
            return (
                <input
                    type="hidden"
                    id={field.field_id}
                    name={field.name}
                    defaultValue={field.value}
                />
            );
        case "select":
        case "select_multiple": {
            const multiple = field.kind === "select_multiple";
            const selected = field.choices
                .filter((c) => c.selected)
                .map((c) => c.value);
            return (
                <select
                    id={field.field_id}
                    name={field.name}
                    required={required}
                    multiple={multiple || undefined}
                    defaultValue={
                        multiple ? selected : (selected[0] ?? undefined)
                    }
                >
                    {field.choices.map((c) => (
                        <option key={c.value} value={c.value}>
                            {c.label}
                        </option>
                    ))}
                </select>
            );
        }
        case "radio":
        case "checkboxes": {
            const inputType = field.kind === "radio" ? "radio" : "checkbox";
            // RadioSelect propagates `required` to each input;
            // CheckboxSelectMultiple does not (matches Django output).
            const subRequired = field.kind === "radio" ? required : undefined;
            return (
                <div id={field.field_id}>
                    {field.choices.map((c) => (
                        <div key={c.value}>
                            <label htmlFor={c.input_id}>
                                <input
                                    id={c.input_id}
                                    name={field.name}
                                    required={subRequired}
                                    type={inputType}
                                    value={c.value}
                                    defaultChecked={c.selected || undefined}
                                />
                                {c.label}
                            </label>
                        </div>
                    ))}
                </div>
            );
        }
        default:
            return (
                <input
                    type={field.input_type || "text"}
                    id={field.field_id}
                    name={field.name}
                    required={required}
                    placeholder={placeholder || undefined}
                    maxLength={num(field.maxlength)}
                    step={field.step || undefined}
                    defaultValue={field.value}
                />
            );
    }
};

/**
 * Renders a Wagtail form-builder form's fields as `<p>{widget}</p>`. Block
 * widgets (radio/checkboxes/password) render a `<div>`; the legacy
 * `<p><div></p>` is re-parsed by the browser into `<p></p><div></div><p></p>`,
 * so we emit that split form directly (valid + hydration-safe + pixel-identical).
 */
export const FormPageFields = ({
    fields,
}: Immutable<{
    fields: FormFieldData[];
}>): React.JSX.Element => {
    return (
        <>
            {fields.map((field) => {
                if (fieldRendersBlock(field)) {
                    return (
                        <React.Fragment key={field.field_id}>
                            <p />
                            <FieldControl field={field} />
                            <p />
                        </React.Fragment>
                    );
                }
                return (
                    <p key={field.field_id}>
                        <FieldControl field={field} />
                    </p>
                );
            })}
        </>
    );
};
